import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from './api';
import type {
  BrandItem,
  CategoryNode,
  ContentResponse,
  ProductDetail,
  ProductList,
  ProductListParams,
  PublicStats,
  SessionUser,
  SuggestItem,
  VehicleOption,
} from '@/types/api';

// Query keys include the language (responses are localized server-side) and, for price-bearing
// data, the session identity so a login/logout never shows cached prices of another viewer.
const viewerKey = (user: SessionUser | null | undefined) => (user ? `${user.id}:${user.priceGroup ?? ''}` : 'anon');

export function useProducts(params: ProductListParams, user: SessionUser | null | undefined) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['products', i18n.language, viewerKey(user), params],
    queryFn: ({ signal }) => api<ProductList>('/products', { query: { ...params }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useProduct(slug: string | undefined, user: SessionUser | null | undefined) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['product', slug, i18n.language, viewerKey(user)],
    queryFn: ({ signal }) => api<ProductDetail>(`/products/${encodeURIComponent(slug!)}`, { signal }),
    enabled: Boolean(slug),
  });
}

export function useSuggestions(q: string) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['suggest', i18n.language, q],
    queryFn: ({ signal }) => api<{ items: SuggestItem[] }>('/products/suggest', { query: { q }, signal }),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  });
}

export function useCategories() {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['categories', i18n.language],
    queryFn: ({ signal }) => api<{ items: CategoryNode[] }>('/categories', { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useBrands() {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['brands', i18n.language],
    queryFn: ({ signal }) => api<{ items: BrandItem[] }>('/brands', { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useStats() {
  return useQuery({
    queryKey: ['stats'],
    queryFn: ({ signal }) => api<PublicStats>('/stats', { signal, withLang: false }),
    staleTime: 5 * 60_000,
  });
}

export function useMakes() {
  return useQuery({
    queryKey: ['vehicles', 'makes'],
    queryFn: ({ signal }) => api<{ items: VehicleOption[] }>('/vehicles/makes', { signal, withLang: false }),
    staleTime: 5 * 60_000,
  });
}

export function useModels(make: string | undefined) {
  return useQuery({
    queryKey: ['vehicles', 'models', make],
    queryFn: ({ signal }) => api<{ items: VehicleOption[] }>('/vehicles/models', { query: { make }, signal, withLang: false }),
    enabled: Boolean(make),
    staleTime: 5 * 60_000,
  });
}

export function useYears(make: string | undefined, model: string | undefined) {
  return useQuery({
    queryKey: ['vehicles', 'years', make, model],
    queryFn: ({ signal }) => api<{ items: number[] }>('/vehicles/years', { query: { make, model }, signal, withLang: false }),
    enabled: Boolean(make && model),
    staleTime: 5 * 60_000,
  });
}

export function useVehicleOptions(make: string | undefined, model: string | undefined, year: number | undefined) {
  return useQuery({
    queryKey: ['vehicles', 'options', make, model, year],
    queryFn: ({ signal }) => api<{ generations: string[]; engines: string[] }>('/vehicles/options', { query: { make, model, year }, signal, withLang: false }),
    enabled: Boolean(make && model),
    staleTime: 5 * 60_000,
  });
}

export function useContent<T = Record<string, string>>(key: string) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['content', key, i18n.language],
    queryFn: ({ signal }) => api<ContentResponse<T>>(`/content/${key}`, { signal }),
    staleTime: 5 * 60_000,
  });
}
